import { Button, Card, Drawer, Empty, Input, Popconfirm, Spin, Tag, message as antdMessage } from "antd";
import { DeleteOutlined, EditOutlined, MessageOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import {
  createTemplate,
  deleteTemplate,
  listTemplates,
  updateTemplate,
} from "../api/followupTemplates";
import type { FollowupTemplate } from "../api/followupTemplates";

export function FollowupTemplatesCard() {
  const [templates, setTemplates] = useState<FollowupTemplate[] | null>(null);
  const [editing, setEditing] = useState<FollowupTemplate | "new" | null>(null);

  const refresh = () => {
    listTemplates()
      .then((d) => setTemplates(d.templates))
      .catch(() => setTemplates([]));
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <Card
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <MessageOutlined /> 群组跟进模板
        </span>
      }
      extra={
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setEditing("new")}
        >
          新建
        </Button>
      }
    >
      <div style={{ fontSize: 14, color: "var(--ant-color-text-secondary)", marginBottom: 12 }}>
        发送群组跟进时可挑选不同场景的模板。变量:{" {{Name}}"}、{" {{Company}}"}、{" {{Email}}"}
      </div>
      {templates === null ? (
        <Spin />
      ) : templates.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: "var(--ant-color-text-secondary)" }}>
              还没有模板,建一个开始用
            </span>
          }
        />
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
          {templates.map((t) => (
            <li
              key={t.id}
              style={{
                border: "1px solid var(--ant-color-border)",
                borderRadius: 10,
                padding: 12,
                background: "var(--ant-color-bg-layout)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                  跟进
                </Tag>
                <span style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>{t.name}</span>
                <Button
                  size="small"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => setEditing(t)}
                  aria-label="编辑"
                />
                <Popconfirm
                  title="删除该模板?"
                  onConfirm={async () => {
                    await deleteTemplate(t.id);
                    antdMessage.success("已删除");
                    refresh();
                  }}
                >
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="删除" />
                </Popconfirm>
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "var(--ant-color-text-secondary)",
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {t.body}
              </div>
            </li>
          ))}
        </ul>
      )}
      <EditDrawer
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />
    </Card>
  );
}

function EditDrawer({
  target,
  onClose,
  onSaved,
}: {
  target: FollowupTemplate | "new" | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const open = target !== null;
  const isNew = target === "new";
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (target && target !== "new") {
      setName(target.name);
      setBody(target.body);
    } else {
      setName("");
      setBody("");
    }
  }, [open, target]);

  const onSave = async () => {
    if (!name.trim() || !body.trim()) {
      antdMessage.warning("名称和正文都要填");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await createTemplate({ name: name.trim(), body });
      } else if (target) {
        await updateTemplate(target.id, { name: name.trim(), body });
      }
      antdMessage.success("已保存");
      onSaved();
    } catch {
      antdMessage.error("保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      title={isNew ? "新建跟进模板" : "编辑跟进模板"}
      open={open}
      onClose={onClose}
      width={Math.min(560, typeof window !== "undefined" ? window.innerWidth : 560)}
      maskClosable={false}
      keyboard={false}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={onSave}>
            保存
          </Button>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <div
            style={{
              fontSize: 14,
              color: "var(--ant-color-text-secondary)",
              marginBottom: 4,
              fontWeight: 500,
            }}
          >
            模板名称
          </div>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如:供应商温和提醒、客户跟进、投资人 Follow-up"
            maxLength={120}
          />
        </div>
        <div>
          <div
            style={{
              fontSize: 14,
              color: "var(--ant-color-text-secondary)",
              marginBottom: 4,
              fontWeight: 500,
            }}
          >
            正文(支持 {"{{Name}} {{Company}} {{Email}}"} 变量)
          </div>
          <Input.TextArea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            autoSize={{ minRows: 6, maxRows: 16 }}
            placeholder="Hi {{Name}}, 想跟进一下之前那封邮件,你那边方便看一眼吗?"
            maxLength={4000}
            showCount
          />
        </div>
      </div>
    </Drawer>
  );
}
